import React, { useEffect, useState } from 'react';
import { Menu } from 'antd';
import { Link, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
    FaBookMedical,
    FaBuilding,
    FaCity,
    FaCog,
    FaConciergeBell,
    FaFileAlt,
    FaFileInvoiceDollar,
    FaFileMedical,
    FaGlobe,
    FaLayerGroup,
    FaList,
    FaMedkit,
    FaMoneyBill,
    FaPlusSquare,
    FaSearch,
    FaTags,
    FaUser,
    FaUserMd,
    FaUserShield,
    FaUsers,
    FaUserTie,
    FaBars,
    FaBriefcaseMedical,
    FaFileInvoice,
    FaHistory,
    FaUserCircle
} from 'react-icons/fa';

import { useAuthStore } from '@/features/auth/store/authStore';
import type { MenuItem, SubMenuItem } from '@/features/auth/types/menuTypes';

const { SubMenu } = Menu;

/* -------------------------------- ICON MAP -------------------------------- */

const iconMap: Record<string, React.ReactElement> = {
    country: <FaGlobe />,
    city: <FaCity />,
    currency: <FaMoneyBill />,
    lookup: <FaSearch />,
    menu: <FaBars />,
    submenu: <FaList />,
    Users: <FaUsers />,
    action: <FaUserShield />,
    setting: <FaCog />,
    plangroup: <FaLayerGroup />,
    servicetype: <FaConciergeBell />,
    plan: <FaFileAlt />,
    company: <FaBuilding />,
    individual: <FaUser />,
    searchpolicy: <FaBookMedical />,
    createpolicy: <FaPlusSquare />,
    providertype: <FaMedkit />,
    MedicalProvider: <FaUserMd />,
    claim: <FaFileInvoiceDollar />,
    caimrequest: <FaFileMedical />,
    category: <FaTags />,
    service: <FaBriefcaseMedical />,
    salesperson: <FaUserTie />,
    icd: <FaBookMedical />,
    Invoice: <FaFileInvoice />,
    customer: <FaUserTie />,
    auditLog: <FaHistory />,
    profile: <FaUserCircle />
};

const getIcon = (iconName?: string | null) => {
    if (!iconName) return null;
    return iconMap[iconName] ?? null;
};

/* ------------------------------ COMPONENT ------------------------------ */

const DynamicMenu: React.FC = () => {
    const { i18n } = useTranslation();
    const menus = useAuthStore((state) => state.menus);
    const location = useLocation();

    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const [openKeys, setOpenKeys] = useState<string[]>([]);

    /* -------------------- AUTO OPEN SUBMENU ON RELOAD -------------------- */

    useEffect(() => {
        if (!menus) return;

        const currentPath = location.pathname;
        let selectedKey: string | undefined;
        let openKey: string | undefined;

        menus.forEach((menu) => {
            const menuKey = menu.name;
            const menuPath = `/${menu.name.toLowerCase().replace(/\s+/g, '-')}`;

            if (currentPath === menuPath) {
                selectedKey = menuKey;
            }

            menu.subMenus?.forEach((sub) => {
                const subKey = `${menu.name}-${sub.name}`;
                const subPath =
                    `/${menu.name.toLowerCase().replace(/\s+/g, '-')}/${sub.name
                        .toLowerCase()
                        .replace(/\s+/g, '-')}`;

                if (currentPath.startsWith(subPath)) {
                    selectedKey = subKey;
                    openKey = menuKey;
                }
            });
        });

        setSelectedKeys(selectedKey ? [selectedKey] : []);
        setOpenKeys(openKey ? [openKey] : []);
    }, [location.pathname, menus]);

    /* ------------------------------ RENDERERS ------------------------------ */

    const renderSubMenu = (sub: SubMenuItem, menuName: string) => {
        const label = i18n.language === 'ar' ? sub.nameAr : sub.subMenuDisplayName;
        const key = `${menuName}-${sub.name}`;
        const path =
            `/${menuName.toLowerCase().replace(/\s+/g, '-')}/${sub.name
                .toLowerCase()
                .replace(/\s+/g, '-')}`;

        return (
            <Menu.Item 
                key={key} 
                icon={getIcon(sub.icon)}
                style={{
                    margin: '4px 12px',
                    borderRadius: 8,
                    height: 40,
                    lineHeight: '40px',
                    paddingLeft: 25
                }}
            >
                <Link to={path} style={{ fontWeight: 400 }}>{label}</Link>
            </Menu.Item>
        );
    };

    const renderMenuItem = (menu: MenuItem) => {
        const label = i18n.language === 'ar' ? menu.nameAr : menu.menuDisplayName;
        const key = menu.name;

        if (menu.subMenus?.length) {
            return (
                <SubMenu 
                    key={key} 
                    icon={getIcon(menu.icon)} 
                    title={<span style={{ fontWeight: 600 }}>{label}</span>}
                    style={{
                        margin: '4px 0',
                        borderRadius: 8
                    }}
                >
                    {menu.subMenus.map((sub) => renderSubMenu(sub, menu.name))}
                </SubMenu>
            );
        }

        const path = `/${menu.name.toLowerCase().replace(/\s+/g, '-')}`;

        return (
            <Menu.Item 
                key={key} 
                icon={getIcon(menu.icon)}
                style={{
                    //margin: '4px 12px',
                    borderRadius: 10,
                    height: 44,
                    lineHeight: '44px',
                    fontSize: 14
                }}
            >
                <Link to={path} style={{ fontWeight: 600 }}>{label}</Link>
            </Menu.Item>
        );
    };

    /* ------------------------------ RENDER ------------------------------ */

    if (!menus) return null;

    return (
        <Menu
            mode="inline"
            selectedKeys={selectedKeys}
            openKeys={openKeys}
            onOpenChange={setOpenKeys}
            style={{
                border: 'none',
                background: 'transparent',
                padding: '8px 0'
            }}
            className="modern-menu"
        >
            {menus.map(renderMenuItem)}
        </Menu>
    );
};

export default DynamicMenu;
